import axios, { AxiosError } from 'axios';
import type { AxiosResponse } from 'axios';
import { Dialog, Toast } from 'vant';
import { BizCode } from '@/shared/constants';
import { detectEnv } from '@/shared/detect';
import { isPlainObject } from 'lodash-es';
import { useUserStore } from '@/stores/modules/user';
import PUBLIC_KEY from '@/publicKey';
import FullscreenLoading from '@/components/loading/fullscreen';
// import { useGlobalStore } from '@/stores/modules/global';
import { NODE_ENV, RUNTIME_ENV, APP_PREFIX, APP_SECURITY } from '@/config';
import yxhJsBridge from '@/shared/yxhJsBridge';

const isProd = RUNTIME_ENV === 'prod';
// const isTest = RUNTIME_ENV === 'testing';

const BIZ_ERROR = 'BizError';

const instance = axios.create({
  baseURL: `${APP_PREFIX}ol-service`,
  headers: {
    timeout: 90000,
    'X-Requested-With': 'XMLHttpRequest',
    'Accept-Language': 'es',
    'X-AppName': 'orale',
    // 'X-AppType': 0, // 0 android, 1 ios
  },
});

/**
 * 控制台统一输出异常
 * @param {Error} error
 */
function outputError(error: AxiosError) {
  const { response, config } = error;

  // eslint-disable-next-line no-console
  console.error(
    '%s\n url: %s\n method: %s\n params: %s\n response: %s',
    error.message,
    config?.url,
    config?.method,
    JSON.stringify(config?.data || config?.params || null),
    JSON.stringify(response?.data),
  );
}

/**
 * 登录失效去登录页
 */
function processLogin() {
  const { pathname, search, hash } = window.location;
  const fullPath = pathname + search + hash;
  if (detectEnv.webview) {
    Dialog.alert({
      message:
        'Su sesión ha expirado o ha iniciado sesión en otro lugar, cierre la página e inicie sesión en la aplicación nuevamente',
      confirmButtonText: 'Cerrar página',
    }).then(() => {
      yxhJsBridge.closeHostWeb();
      return;
    });
    return;
  }
  window.__g_router__.replace({
    path: '/login',
    query: {
      redirect: encodeURIComponent(fullPath),
    },
  });
}

/**
 * 用户信息设置
 */
instance.interceptors.request.use(async (config) => {
  // eslint-disable-next-line no-underscore-dangle
  const loading = config.__interceptorOptions__?.loading ?? true;

  if (loading) {
    FullscreenLoading.show();
  }

  const { token, isLogin, commonData } = useUserStore();

  if (config.headers) {
    if (isLogin && token != null) {
      config.headers['X-Token'] = token;
    }
    if (commonData?.softwareBuild) {
      config.headers['X-AppVersion'] = commonData?.softwareBuild;
    }
  }

  return config;
});

/**
 * 参数加密
 */
instance.interceptors.request.use((config) => {
  const security = config.__interceptorOptions__?.security ?? false;

  /**
   * 以下场景设置默认参数
   * 1. 启用了加密
   * 2. 参数没传
   * 3. 请求为POST
   *
   * TODO: 之所以这么处理是因为Spring中的 @RequestBody 注解默认为必传，
   * 如果改为 @RequestBody(required = false) 则影响范围较大，因此在拦截器层面对于此场景填充默认参数
   */
  if (security && config.data == null && config.method?.toUpperCase() === 'POST') {
    config.data = {};
  }

  if (isProd && APP_SECURITY === 'true' && security) {
    // if (isTest && APP_SECURITY === 'true' && security) {
    try {
      const { aesKey, data } = CECryptojs.RSAWithAES.encrypt(
        PUBLIC_KEY,
        JSON.stringify(config.data ?? {}),
        'AES-CBC',
      );

      config.__aesKey__ = aesKey;
      config.data = {
        ivKey: data.nonce,
        encryptData: data.ciphertext,
        mainLock: data.key,
        algMethod: data.algorithm,
      };
    } catch {}
  }

  return config;
});

/**
 * 返回数据解密
 */
instance.interceptors.response.use((response) => {
  const security = response.config.__interceptorOptions__?.security ?? false;

  if (isProd && APP_SECURITY === 'true' && security) {
    // if (isTest && APP_SECURITY === 'true' && security) {
    try {
      const aesKey = response.config.__aesKey__ ?? '';
      const { encryptData, ivKey } = response.data ?? {};
      response.data = JSON.parse(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        CECryptojs.AES.decrypt(aesKey, ivKey, encryptData, 'AES-CBC'),
      );
    } catch {}
  }

  return response;
});

/**
 * 处理业务状态码
 */
instance.interceptors.response.use(
  (response) => {
    FullscreenLoading.hide();

    const result: { resultStatus: BizCode; resultMsg: string; resultInfo?: any } = response.data;

    if (result.resultStatus === BizCode.SUCCESS) {
      return result.resultInfo;
    }

    // const error = new Error(
    //   `Request failed with biz status code ${result.resultStatus}${result.resultMsg ? `, ${result.resultMsg}` : ''}`,
    // );
    const error = new Error(`${result.resultMsg ? `${result.resultMsg}` : ''}`);

    error.name = BIZ_ERROR;
    error.response = response;

    const { toast = true, redirectToLogin = true } = response.config?.__interceptorOptions__ ?? {};

    if (NODE_ENV === 'development') {
      outputError(
        new AxiosError(error.message, error.name, response.config, response.request, response),
      );
    }

    // 未登录或登录失效
    if (result.resultStatus === BizCode.UNAUTHORIZED) {
      const userStore = useUserStore();
      userStore.logout();

      if (redirectToLogin) {
        processLogin();
      }
    } else if (toast) {
      Toast.fail(result.resultMsg || 'Error desconocido');
    }

    return Promise.reject(error);
  },
  (error: AxiosError) => {
    FullscreenLoading.hide();

    if (!isPlainObject(error.response)) {
      Toast.fail(
        'Se ha producido un error desconocido, es posible que haya un problema con su red.',
      );
      return Promise.reject(error);
    }

    const { status, statusText, config } = error.response as AxiosResponse;
    const { toast = true, redirectToLogin = true } = config?.__interceptorOptions__ ?? {};

    if (status === 401) {
      const userStore = useUserStore();
      userStore.logout();

      if (redirectToLogin) {
        processLogin();
      }
    } else if (toast) {
      if (status === 403) {
        Toast.fail('No tiene permiso para acceder.');
      } else {
        Toast.fail(statusText || 'Error desconocido');
      }
    }

    if (NODE_ENV === 'development') {
      outputError(error);
    }

    return Promise.reject(error);
  },
);

export default instance;
